import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useFavorites } from "@/context/FavoritesContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import FavoriteButton from "@/components/FavoriteButton";
import { Heart, Activity, MapPin, Target, AlertCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

export default function Favorites() {
  const { t, lang } = useLang();
  const { ids, clear } = useFavorites();
  const [tests, setTests] = useState([]);
  const [regionMap, setRegionMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancel = false;
    async function load() {
      setLoading(true);
      try {
        const [all, regs] = await Promise.all([
          api.get("/tests"),
          api.get("/regions"),
        ]);
        if (cancel) return;
        setTests(all.data.filter((tst) => ids.includes(tst.id)));
        const m = {};
        regs.data.forEach((r) => { m[r.id] = r.name; });
        setRegionMap(m);
      } catch (e) {
        toast.error(t("fav.errLoad"));
      } finally {
        if (!cancel) setLoading(false);
      }
    }
    load();
    return () => { cancel = true; };
  }, [ids, lang, t]);

  const grouped = useMemo(() => {
    const g = {};
    tests.forEach((tst) => {
      g[tst.region] = g[tst.region] || [];
      g[tst.region].push(tst);
    });
    return g;
  }, [tests]);

  return (
    <div data-testid="favorites-page">
      <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3 font-mono">
        <span className="title-rule" />{t("fav.eyebrow")}
      </div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight">{t("fav.title")}</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {t("fav.lead1")} <Heart className="inline size-3.5 -mt-0.5 text-accent" /> {t("fav.lead2")}
          </p>
        </div>
        {tests.length > 0 && (
          <button
            onClick={() => { clear(); toast.success(t("fav.cleared")); }}
            className="text-xs text-muted-foreground hover:text-destructive"
            data-testid="fav-clear-btn"
          >
            {t("fav.clearAll")}
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-muted-foreground">{t("common.loading")}</div>
      ) : tests.length === 0 ? (
        <Card className="p-10 text-center bg-card border-dashed" data-testid="fav-empty">
          <Heart className="size-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-base font-serif font-medium mb-1">{t("fav.emptyTitle")}</p>
          <p className="text-sm text-muted-foreground mb-5">
            {t("fav.emptyLead")}
          </p>
          <Link to="/" className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-full text-sm">
            {t("fav.explore")}
          </Link>
        </Card>
      ) : (
        <>
          <Card className="p-4 mb-6 bg-card border-accent/30">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="size-9 rounded-md bg-accent/15 text-accent flex items-center justify-center">
                  <Sparkles className="size-4" />
                </div>
                <div className="text-sm">
                  <div className="font-medium">{t("fav.countLabel", { n: tests.length })}</div>
                  <div className="text-xs text-muted-foreground">{t("fav.useAsDx")}</div>
                </div>
              </div>
              <Link to="/differentialdiagnos" data-testid="fav-to-dx-btn">
                <Button size="sm">{t("fav.toDx")}</Button>
              </Link>
            </div>
          </Card>

          {Object.entries(grouped).map(([reg, list]) => (
            <div key={reg} className="mb-7">
              <h3 className="font-serif text-lg font-medium mb-3 text-primary">
                {regionMap[reg] || reg}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {list.map((tst) => (
                  <Card
                    key={tst.id}
                    onClick={() => setSelected(tst)}
                    data-testid={`fav-test-${tst.id}`}
                    className="p-4 cursor-pointer hover:border-accent/60 transition-colors flex items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-base font-medium leading-tight">{tst.name}</div>
                      <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{tst.purpose}</div>
                    </div>
                    <Badge variant="secondary" className="font-mono text-[10px]">{tst.position}</Badge>
                    <FavoriteButton testId={tst.id} />
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl bg-card">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">{selected.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <Row icon={Target} label={t("test.purpose")} text={selected.purpose} />
                <Row icon={Activity} label={t("test.execution")} text={selected.execution} />
                <Row icon={AlertCircle} label={t("test.positive")} text={selected.positive} accent />
                {selected.comment && <Row icon={MapPin} label={t("test.comment")} text={selected.comment} />}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ icon: Icon, label, text, accent }) {
  const isNumberedSteps = /^\s*1\.\s.+?(?:\s+2\.\s)/.test(text || "");
  const steps = isNumberedSteps
    ? (text.match(/\d+\.\s+[^]*?(?=\s+\d+\.\s|$)/g) || []).map((s) => s.replace(/^\d+\.\s+/, "").trim())
    : null;

  return (
    <div>
      <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
        <Icon className={`size-3.5 ${accent ? "text-accent" : ""}`} />{label}
      </div>
      {steps ? (
        <ol className={`text-sm leading-relaxed space-y-1.5 ${accent ? "font-medium text-foreground" : "text-foreground/85"}`}>
          {steps.map((s, i) => (
            <li key={i} className="flex gap-2.5">
              <span className="font-mono text-muted-foreground tabular-nums shrink-0 min-w-[1.25rem]">{i + 1}.</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className={`text-sm leading-relaxed ${accent ? "font-medium text-foreground" : "text-foreground/85"}`}>{text}</p>
      )}
    </div>
  );
}
